<template>
  <b-row class="commission-item-search">
    <b-col :md="typeCols">
      <b-form-select
        v-model="selectedSearchType"
        :options="searchTypeOptions"
        size="sm"
        value-field="id"
        text-field="name"
        @change="onChangeSearchType"
      ></b-form-select>
    </b-col>
    <b-col>
      <vue-autosuggest
        v-model="searchItem"
        :suggestions="filteredItemOptions"
        @selected="onSelectedItem"
        :limit="10"
        @input="onInputChangeItem"
        :input-props="{
          id: inputId,
          class: 'form-control form-control-sm',
          placeholder: searchPlaceholder,
          autocomplete: 'off',
        }"
        :should-render-suggestions="(size, loading) => size >= 0 && !loading"
      >
        <template slot-scope="{ suggestion }">
          <div>{{ suggestion.item.name }}</div>
        </template>
      </vue-autosuggest>
    </b-col>
  </b-row>
</template>

<script>
/**
 * Ô tìm sản phẩm / danh mục dùng chung cho các màn nhóm hoa hồng.
 * Phát sự kiện `selected` với { itemType, item } — itemType theo COMMISSION_CATEGORY_ITEM_TYPE.
 *
 * Mỗi lần gọi gợi ý được đánh số; kết quả về muộn (gõ tiếp, đổi loại tìm) bị bỏ qua,
 * nên không bao giờ hiện gợi ý của loại cũ dưới loại mới.
 */
import ApiService from '@/core/services/api.service';
import decounce from 'debounce';
import { TIME_TRIGGER } from '@/utils/constants';
import { makeToastFaile } from '@/utils/common';
import {
  COMMISSION_CATEGORY_ITEM_TYPE,
  COMMISSION_SEARCH_TYPE,
} from './commission.constants';

const HTTP_FORBIDDEN = 403;

export default {
  props: {
    inputId: {
      type: String,
      default: 'commission-item-search__input',
    },
    typeCols: {
      type: [String, Number],
      default: 4,
    },
    /** true: chọn xong xoá ô tìm (thêm nhiều item liên tiếp); false: giữ tên vừa chọn */
    clearOnSelect: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      searchTypeOptions: [
        { id: COMMISSION_SEARCH_TYPE.PRODUCT_IMEI, name: 'Sản phẩm IMEI' },
        { id: COMMISSION_SEARCH_TYPE.PRODUCT, name: 'Sản phẩm' },
        { id: COMMISSION_SEARCH_TYPE.CATEGORY, name: 'Danh mục' },
      ],
      selectedSearchType: COMMISSION_SEARCH_TYPE.PRODUCT_IMEI,
      searchItem: '',
      filteredItemOptions: [{ data: [] }],
      requestSeq: 0,
    };
  },
  created() {
    // Tạo riêng cho từng component (methods bị Vue bind lại sẽ mất .clear())
    this.debounceSearchItem = decounce(() => {
      this.fetchItemSuggestions(this.searchItem);
    }, TIME_TRIGGER);
  },
  beforeDestroy() {
    this.debounceSearchItem.clear();
  },
  computed: {
    searchPlaceholder() {
      return this.selectedSearchType === COMMISSION_SEARCH_TYPE.CATEGORY
        ? 'Nhập tên danh mục'
        : 'Nhập tên sản phẩm';
    },
  },
  methods: {
    /** Xoá ô tìm + bỏ mọi kết quả gợi ý đang chờ */
    clear() {
      this.requestSeq += 1;
      this.debounceSearchItem.clear();
      this.searchItem = '';
      this.filteredItemOptions = [{ data: [] }];
    },
    onChangeSearchType() {
      this.clear();
      this.$emit('change-type');
    },
    onInputChangeItem(text) {
      this.searchItem = text;
      this.debounceSearchItem();
    },
    fetchItemSuggestions(textSearch) {
      this.requestSeq += 1;
      const seq = this.requestSeq;
      ApiService.setHeader();
      ApiService.get(
        `productSearch/search-by-product-category?searchProduct=${encodeURIComponent(
          textSearch || '',
        )}&typeSearch=${this.selectedSearchType}&typeOfProduct=0`,
      )
        .then(({ data }) => {
          if (seq === this.requestSeq) {
            this.filteredItemOptions = [{ data: data.data || [] }];
          }
        })
        .catch((error) => {
          if (seq !== this.requestSeq) {
            return;
          }
          this.filteredItemOptions = [{ data: [] }];
          if (error && error.response && error.response.status === HTTP_FORBIDDEN) {
            makeToastFaile('Bạn chưa có quyền xem sản phẩm nên không tìm được!');
          }
        });
    },
    onSelectedItem(option) {
      if (!option || !option.item) {
        return;
      }
      const itemType =
        this.selectedSearchType === COMMISSION_SEARCH_TYPE.CATEGORY
          ? COMMISSION_CATEGORY_ITEM_TYPE.CATEGORY
          : COMMISSION_CATEGORY_ITEM_TYPE.PRODUCT;
      const { item } = option;
      if (this.clearOnSelect) {
        this.clear();
      } else {
        this.requestSeq += 1;
        this.searchItem = item.name;
        this.filteredItemOptions = [{ data: [] }];
      }
      this.$emit('selected', { itemType, item });
    },
  },
};
</script>

<style scoped>
/* Danh sách gợi ý nổi đè lên nội dung bên dưới, giống dropdown của b-form-select */
.commission-item-search ::v-deep .autosuggest__results-container {
  position: relative;
}
.commission-item-search ::v-deep .autosuggest__results {
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 0.42rem;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}
.commission-item-search ::v-deep .autosuggest__results ul {
  list-style: none;
  margin: 0;
  padding: 4px 0;
}
.commission-item-search ::v-deep .autosuggest__results-item {
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
}
.commission-item-search ::v-deep .autosuggest__results-item:hover,
.commission-item-search ::v-deep .autosuggest__results-item--highlighted {
  background: #f3f6f9;
}
</style>
